import React, { useState, useRef } from 'react';
import { 
  X, 
  Upload as UploadIcon, 
  FileText, 
  Download, 
  ExternalLink, 
  ChevronRight, 
  Check,
  AlertCircle,
  Loader2
} from 'lucide-react';
import { cn } from '../lib/utils';

interface BankStatementUploadProps {
  onClose: () => void;
  onComplete: (data: any) => void;
  accountName: string;
  currency: string;
  lastTransaction?: {
    date: string;
    description: string;
    amount: number;
  };
}

interface ImportedStatementLine {
  date: string;
  amount: number;
  payee: string;
  description: string;
  reference: string;
  checkNumber: string;
}

const csvRows = (text: string) => {
  const rows: string[][] = [];
  let row: string[] = [];
  let value = '';
  let quoted = false;
  for (let index = 0; index < text.length; index += 1) {
    const character = text[index];
    if (character === '"') {
      if (quoted && text[index + 1] === '"') {
        value += '"';
        index += 1;
      } else {
        quoted = !quoted;
      }
    } else if (character === ',' && !quoted) {
      row.push(value.trim());
      value = '';
    } else if ((character === '\n' || character === '\r') && !quoted) {
      if (character === '\r' && text[index + 1] === '\n') index += 1;
      row.push(value.trim());
      if (row.some(cell => cell.length > 0)) rows.push(row);
      row = [];
      value = '';
    } else {
      value += character;
    }
  }
  row.push(value.trim());
  if (row.some(cell => cell.length > 0)) rows.push(row);
  return rows;
};

const asIsoDate = (value: string) => {
  const trimmed = value.trim();
  const iso = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (iso) return trimmed;
  const slash = trimmed.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
  if (slash) {
    const month = String(Number(slash[1])).padStart(2, '0');
    const day = String(Number(slash[2])).padStart(2, '0');
    return `${slash[3]}-${month}-${day}`;
  }
  return '';
};

const parseStatementCsv = (text: string): ImportedStatementLine[] => {
  const [headerRow, ...dataRows] = csvRows(text);
  if (!headerRow?.length) return [];
  const headers = headerRow.map(header => header.toLowerCase().replace(/[^a-z0-9]/g, ''));
  const column = (...names: string[]) => headers.findIndex(header => names.includes(header));
  const dateColumn = column('date', 'transactiondate');
  const amountColumn = column('amount', 'transactionamount');
  if (dateColumn < 0 || amountColumn < 0) {
    throw new Error('CSV must include Date and Amount columns.');
  }
  const payeeColumn = column('by', 'payee', 'merchant', 'contact');
  const descriptionColumn = column('description', 'memo', 'details');
  const referenceColumn = column('reference', 'referencenumber', 'transactionid');
  const checkNumberColumn = column('checknumber', 'checkno', 'chequenumber');

  return dataRows.flatMap(row => {
    const date = asIsoDate(row[dateColumn] || '');
    const amount = Number((row[amountColumn] || '').replace(/[$,\s]/g, ''));
    if (!date || !Number.isFinite(amount) || amount === 0) return [];
    const payee = payeeColumn >= 0 ? row[payeeColumn] || '' : '';
    const description = descriptionColumn >= 0 ? row[descriptionColumn] || '' : payee;
    return [{
      date,
      amount,
      payee,
      description,
      reference: referenceColumn >= 0 ? row[referenceColumn] || '' : '',
      checkNumber: checkNumberColumn >= 0 ? row[checkNumberColumn] || '' : ''
    }];
  });
};

export default function BankStatementUpload({ 
  onClose, 
  onComplete, 
  accountName, 
  currency,
  lastTransaction = {
    date: 'Feb 28, 2026',
    description: 'WPS Fees Dr Feb-2026 SIF17203240QIB202602281134.csv',
    amount: -100.00
  }
}: BankStatementUploadProps) {
  const [step, setStep] = useState(1);
  const [file, setFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [importedLines, setImportedLines] = useState<ImportedStatementLine[]>([]);
  const [importError, setImportError] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
      setImportedLines([]);
      setImportError('');
    }
  };

  const handleUpload = async () => {
    if (!file) return;
    setIsUploading(true);
    setImportError('');
    try {
      if (!file.name.toLowerCase().endsWith('.csv')) {
        throw new Error('Local statement import currently supports CSV files.');
      }
      const lines = parseStatementCsv(await file.text());
      if (!lines.length) {
        throw new Error('No valid statement lines were found in this CSV file.');
      }
      setImportedLines(lines);
      setStep(2);
    } catch (error) {
      setImportError(error instanceof Error ? error.message : 'Could not read the statement file.');
    } finally {
      setIsUploading(false);
    }
  };

  const renderStepIndicator = () => (
    <div className="flex items-center justify-center gap-4 mb-12">
      {[1, 2, 3].map((s) => (
        <React.Fragment key={s}>
          <div className="flex items-center gap-2">
            <div className={cn(
              "w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all",
              step === s ? "bg-[#141414] text-[#E4E3E0]" : 
              step > s ? "bg-green-500 text-white" : "bg-[#141414]/10 text-[#141414]/40"
            )}>
              {step > s ? <Check className="w-4 h-4" /> : s}
            </div>
            <span className={cn(
              "text-sm font-bold",
              step === s ? "opacity-100" : "opacity-40"
            )}>
              {s === 1 ? 'Upload' : s === 2 ? 'Import settings' : 'Review'}
            </span>
          </div>
          {s < 3 && <div className="w-12 h-[2px] bg-[#141414]/10" />}
        </React.Fragment>
      ))}
    </div>
  );

  return (
    <div
      className="fixed inset-0 overflow-y-auto overscroll-contain bg-[#141414]/40 backdrop-blur-sm z-[110] flex items-center justify-center p-4"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="bg-[#E4E3E0] border border-[#141414] w-full max-w-4xl rounded-[40px] p-12 shadow-2xl relative overflow-hidden flex flex-col max-h-[calc(100dvh-2rem)]">
        <button 
          onClick={onClose}
          className="absolute top-8 right-8 p-2 hover:bg-[#141414] hover:text-[#E4E3E0] rounded-full transition-colors"
        >
          <X className="w-6 h-6" />
        </button>

        {renderStepIndicator()}

        {step === 1 && (
          <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
            <div>
              <h2 className="text-4xl font-bold tracking-tight mb-4">Prepare file for upload</h2>
              <p className="text-lg opacity-60 italic serif leading-relaxed max-w-2xl">
                Export a CSV bank statement from your online banking, then map and review its transaction lines before importing.
              </p>
            </div>

            <div className="space-y-4">
              <p className="text-sm font-medium opacity-80">
                Alternatively, create your own statement file with our CSV template.
              </p>
              <div className="flex flex-wrap gap-6">
                <button className="flex items-center gap-2 text-blue-600 hover:underline font-bold text-sm">
                  Download Accountix CSV template (CSV, 56 B) <ExternalLink className="w-4 h-4" />
                </button>
                <button className="flex items-center gap-2 text-blue-600 hover:underline font-bold text-sm">
                  Learn more about importing transactions <ExternalLink className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-sm font-bold uppercase tracking-widest opacity-40">Most recent transaction</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 border border-[#141414] rounded-2xl overflow-hidden bg-white/30">
                <div className="p-6 border-r border-[#141414] md:col-span-2">
                  <p className="text-sm font-bold mb-1">{lastTransaction.date}</p>
                  <p className="text-sm opacity-60 font-mono break-all">{lastTransaction.description}</p>
                </div>
                <div className="grid grid-cols-2 divide-x divide-[#141414]">
                  <div className="p-6 flex flex-col justify-between">
                    <p className="text-[10px] uppercase font-bold opacity-40 text-center">Spent</p>
                    <p className="text-sm font-bold text-center mt-2">
                      {lastTransaction.amount < 0 ? `${currency} ${Math.abs(lastTransaction.amount).toFixed(2)}` : ''}
                    </p>
                  </div>
                  <div className="p-6 flex flex-col justify-between">
                    <p className="text-[10px] uppercase font-bold opacity-40 text-center">Received</p>
                    <p className="text-sm font-bold text-center mt-2">
                      {lastTransaction.amount > 0 ? `${currency} ${lastTransaction.amount.toFixed(2)}` : ''}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-sm font-bold uppercase tracking-widest opacity-40">Example CSV Format</h3>
              <div className="bg-white/50 border border-[#141414] p-6 rounded-2xl overflow-x-auto">
                <table className="w-full text-[10px] font-mono">
                  <thead>
                    <tr className="border-b border-[#141414]/10">
                      <th className="text-left py-2 pr-4">Date</th>
                      <th className="text-left py-2 pr-4">Description</th>
                      <th className="text-right py-2 pr-4 text-red-600">Spend</th>
                      <th className="text-right py-2 text-green-600">Receive</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-[#141414]/5">
                      <td className="py-2 pr-4">2026-03-01</td>
                      <td className="py-2 pr-4">Starbucks Coffee</td>
                      <td className="py-2 pr-4 text-right">12.50</td>
                      <td className="py-2 text-right">-</td>
                    </tr>
                    <tr>
                      <td className="py-2 pr-4">2026-03-02</td>
                      <td className="py-2 pr-4">Client Payment</td>
                      <td className="py-2 pr-4 text-right">-</td>
                      <td className="py-2 text-right">1500.00</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div className="flex justify-center">
                <button 
                  onClick={() => {
                    const headers = ['Date', 'Amount', 'By', 'Description', 'Reference', 'Check Number'];
                    const csvContent = [
                      headers.join(','),
                      '2026-03-01,-12.50,Starbucks,Coffee,TXN-001,',
                      '2026-03-02,1500.00,Client A,Invoice Payment,INV-100,',
                      '2026-03-03,-2000.00,Landlord,Monthly Rent,RENT-MAR,102'
                    ].join('\n');
                    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
                    const link = document.createElement('a');
                    const url = URL.createObjectURL(blob);
                    link.setAttribute('href', url);
                    link.setAttribute('download', 'bank_statement_template.csv');
                    link.style.visibility = 'hidden';
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                  }}
                  className="flex items-center gap-2 border border-[#141414] px-6 py-2 rounded-full hover:bg-[#141414] hover:text-[#E4E3E0] transition-all font-bold text-sm"
                >
                  <Download className="w-4 h-4" />
                  <span>Download Template</span>
                </button>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-sm font-bold uppercase tracking-widest opacity-40">File to upload</h3>
              <div 
                onClick={() => fileInputRef.current?.click()}
                className={cn(
                  "border-2 border-dashed border-[#141414]/20 rounded-3xl p-12 text-center cursor-pointer hover:border-[#141414] hover:bg-white/20 transition-all group relative",
                  file && "border-green-500 bg-green-50/50"
                )}
              >
                <input 
                  type="file" 
                  ref={fileInputRef}
                  className="hidden" 
                  accept=".csv"
                  onChange={handleFileSelect}
                />
                {!file ? (
                  <>
                    <div className="w-16 h-16 bg-[#141414]/5 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                      <UploadIcon className="w-8 h-8 opacity-40" />
                    </div>
                    <p className="text-xl font-bold italic serif mb-2">Drag and drop file or select manually</p>
                    <button 
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        fileInputRef.current?.click();
                      }}
                      className="bg-[#141414] text-[#E4E3E0] px-8 py-3 rounded-full font-bold mt-4 hover:opacity-90 transition-opacity relative z-10"
                    >
                      Select file
                    </button>
                  </>
                ) : (
                  <div className="flex items-center justify-center gap-4">
                    <div className="p-4 bg-green-100 text-green-600 rounded-2xl">
                      <FileText className="w-8 h-8" />
                    </div>
                    <div className="text-left">
                      <p className="text-lg font-bold">{file.name}</p>
                      <p className="text-sm opacity-50">{(file.size / 1024).toFixed(2)} KB</p>
                    </div>
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        setFile(null);
                      }}
                      className="ml-4 p-2 hover:bg-red-100 text-red-500 rounded-full transition-colors"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                )}
              </div>
              <p className="text-xs opacity-40 italic">For local PostgreSQL mode, export your statement as CSV.</p>
              {importError && (
                <p className="text-sm text-red-600 font-medium">{importError}</p>
              )}
            </div>

            <div className="flex justify-end pt-4">
              <button 
                disabled={!file || isUploading}
                onClick={handleUpload}
                className="bg-[#141414] text-[#E4E3E0] px-12 py-4 rounded-full font-bold hover:opacity-90 transition-opacity disabled:opacity-30 flex items-center gap-2"
              >
                {isUploading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Continue'}
              </button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-8 animate-in fade-in slide-in-from-right-4 duration-500">
            <div>
              <h2 className="text-4xl font-bold tracking-tight mb-4">Import settings</h2>
              <p className="text-lg opacity-60 italic serif leading-relaxed">
                Map the columns from your file to the system fields.
              </p>
            </div>

            <div className="bg-white/30 border border-[#141414] rounded-3xl overflow-hidden">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-[#141414] text-[#E4E3E0] uppercase text-[10px] tracking-widest font-bold dark:bg-[#000000] border-b dark:border-white/10">
                    <th className="px-6 py-4">System Field</th>
                    <th className="px-6 py-4">File Column</th>
                    <th className="px-6 py-4">Example Value</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#141414]/10">
                  {[
                    { field: 'Date*', column: 'Date', value: '2026-03-01' },
                    { field: 'Amount*', column: 'Amount', value: '-120.50' },
                    { field: 'Payee', column: 'Payee', value: 'Starbucks' },
                    { field: 'Description', column: 'Description', value: 'Coffee' },
                    { field: 'Reference', column: 'Reference', value: 'TXN-998' },
                    { field: 'Check Number', column: 'Check Number', value: '' },
                  ].map((row, i) => (
                    <tr key={i}>
                      <td className="px-6 py-4 font-bold text-sm">{row.field}</td>
                      <td className="px-6 py-4">
                        <select className="bg-white/50 border border-[#141414] px-3 py-2 rounded-lg text-sm w-full outline-none">
                          <option>{row.column}</option>
                          <option>Other Column</option>
                        </select>
                      </td>
                      <td className="px-6 py-4 text-sm opacity-50 font-mono">{row.value || 'N/A'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex justify-between pt-4">
              <button 
                onClick={() => setStep(1)}
                className="px-8 py-4 rounded-full font-bold hover:bg-[#141414]/5 transition-all"
              >
                Back
              </button>
              <button 
                onClick={() => setStep(3)}
                className="bg-[#141414] text-[#E4E3E0] px-12 py-4 rounded-full font-bold hover:opacity-90 transition-opacity"
              >
                Continue
              </button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-8 animate-in fade-in slide-in-from-right-4 duration-500">
            <div>
              <h2 className="text-4xl font-bold tracking-tight mb-4">Review transactions</h2>
              <p className="text-lg opacity-60 italic serif leading-relaxed">
                {`We found ${importedLines.length} ${importedLines.length === 1 ? 'transaction' : 'transactions'} in your file. Review them before importing.`}
              </p>
            </div>

            <div className="bg-white/30 border border-[#141414] rounded-3xl overflow-hidden max-h-[400px] overflow-y-auto custom-scrollbar">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-[#141414] text-[#E4E3E0] uppercase text-[10px] tracking-widest font-bold sticky top-0 z-10 dark:bg-[#000000] border-b dark:border-white/10">
                    <th className="px-6 py-4">Date</th>
                    <th className="px-6 py-4">Description</th>
                    <th className="px-6 py-4 text-right">Amount</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#141414]/10">
                  {importedLines.map((tx, i) => (
                    <tr key={`${tx.date}-${tx.reference}-${i}`} className="hover:bg-white/20 transition-colors">
                      <td className="px-6 py-4 text-sm font-mono">{tx.date}</td>
                      <td className="px-6 py-4 text-sm font-bold">{tx.description || tx.payee || 'Statement line'}</td>
                      <td className={cn(
                        "px-6 py-4 text-right text-sm font-bold font-mono",
                        tx.amount > 0 ? "text-green-600" : "text-red-600"
                      )}>
                        {tx.amount > 0 ? '+' : ''}{tx.amount.toFixed(2)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="bg-blue-50 border border-blue-200 p-6 rounded-3xl flex items-start gap-4">
              <AlertCircle className="w-6 h-6 text-blue-600 shrink-0 mt-1" />
              <div>
                <p className="text-sm font-bold text-blue-900">Ready to import</p>
                <p className="text-sm text-blue-800 opacity-80 italic serif">
                  {`These transactions will be added to ${accountName}. You can reconcile them in the next step.`}
                </p>
              </div>
            </div>

            <div className="flex justify-between pt-4">
              <button 
                onClick={() => setStep(2)}
                className="px-8 py-4 rounded-full font-bold hover:bg-[#141414]/5 transition-all"
              >
                Back
              </button>
              <button 
                onClick={() => onComplete({
                  fileName: file?.name,
                  transactionCount: importedLines.length,
                  lines: importedLines
                })}
                className="bg-[#141414] text-[#E4E3E0] px-12 py-4 rounded-full font-bold hover:opacity-90 transition-opacity"
              >
                Complete Import
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
